import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listMySupportTickets } from "@/lib/support.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/support/")({
  head: () => ({
    meta: [
      { title: "Support center — The Hospitality Reserve" },
      { name: "description", content: "View your past support tickets, statuses, and email timestamps." },
    ],
  }),
  component: SupportCenter,
});

type Ticket = Awaited<ReturnType<typeof listMySupportTickets>>[number];

function statusVariant(s: string): "default" | "secondary" | "outline" {
  if (s === "resolved") return "default";
  if (s === "reviewed") return "secondary";
  return "outline";
}

function fmt(d: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

function SupportCenter() {
  const fetchList = useServerFn(listMySupportTickets);
  const [items, setItems] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchList()
      .then((r) => setItems(r))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [fetchList]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors">
          ← Back
        </Link>
        <h1 className="font-display text-2xl mt-2">Support center</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Every support request you send is tracked here with its current status and email delivery timestamp.
        </p>

        {loading ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="mt-6 text-sm text-destructive">{error}</p>
        ) : items.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground">
            You haven't sent any tickets yet. Use the support button in the bottom-right of any page.
          </p>
        ) : (
          <ul className="mt-6 space-y-3">
            {items.map((t) => (
              <li key={t.id} className="rounded-lg border border-border/60 p-4 hover:bg-muted/30 transition-colors">
                <Link
                  to="/support/$code"
                  params={{ code: t.ticket_code }}
                  className="flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground font-mono">{t.ticket_code}</div>
                      <div className="font-medium truncate">{t.subject}</div>
                    </div>
                    <Badge variant={statusVariant(t.status)} className="capitalize shrink-0">
                      {t.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-3 gap-1">
                    <span>Sent: {fmt(t.created_at)}</span>
                    <span>Reviewed: {fmt(t.reviewed_at)}</span>
                    <span>Resolved: {fmt(t.resolved_at)}</span>
                  </div>
                  {t.email_status && (
                    <div className="text-[11px] text-muted-foreground">
                      Email: {t.email_status}
                      {t.email_sent_at ? ` · ${fmt(t.email_sent_at)}` : ""}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
